import React, { useState } from 'react';
import {
  SelectMenu,
  Switch,
  TextField,
  Typography,
  SegmentedButtons,
  Accordion,
} from '@scale/scaleui';
import { MultiTurnInteractionConfiguration } from '@scale/shared/types/chat';
import produce from 'immer';
import _ from 'lodash';
import { ImageExtension, ImageCategory, DEFAULT_CATEGORY_BLOCKLIST } from '@scale/mm-shared';
import ConfigSectionHeader from '../components/ConfigSectionHeader';
import ImageServiceConfigurator from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatConfigurationEditor/components/ImageServiceConfigurator';

interface ImageReferenceBuilderProps {
  name: string;
  existingValue: MultiTurnInteractionConfiguration;
  updateProperty: (arg: MultiTurnInteractionConfiguration) => void;
  useAccordion?: boolean;
}

enum DomainRestriction {
  AllowedDomains = 'Allowed Domains',
  BlockedDomains = 'Blocked Domains',
}

/* eslint-disable complexity */
const ImageReferenceBuilder = ({
  name,
  existingValue,
  updateProperty,
  useAccordion = true,
}: ImageReferenceBuilderProps) => {
  const WrappingComponent = useAccordion ? Accordion : ConfigSectionHeader;
  const config = existingValue;

  const templateVariablesOptions = Object.keys(existingValue?.templateVariables ?? {}).map(
    (name: string) => {
      return { name, id: name };
    },
  );

  const [allowedExtensions, setAllowedExtensions] = useState<ImageExtension[]>(
    // TODO: config.allowedExtensions is deprecated, eventually remove it here
    config.imageValidatorSettings?.allowedExtensions ||
      config.allowedExtensions ||
      (Object.values(ImageExtension) as ImageExtension[]),
  );

  const [allowedCategories, setAllowedCategories] = useState<ImageCategory[]>(
    config.imageValidatorSettings?.allowedCategories ??
      Object.values(ImageCategory).filter(
        category => !DEFAULT_CATEGORY_BLOCKLIST.includes(category),
      ),
  );

  const [domainRestriction, setDomainRestriction] = useState<string>(
    config.imageValidatorSettings?.allowedDomainsList
      ? DomainRestriction.AllowedDomains
      : DomainRestriction.BlockedDomains,
  );

  return (
    <WrappingComponent header={name}>
      <div className="space-y-2">
        <SegmentedButtons
          buttons={[
            {
              icon: ['far', 'user'],
              id: 'customer',
              label: 'Image from Customer',
            },
            {
              icon: ['far', 'users'],
              id: 'user',
              label: 'Image from Contributor',
            },
          ]}
          onSelect={id => {
            updateProperty({
              ...config,
              imageProvidedByCustomer: id === 'customer',
            });
          }}
          selected={config.imageProvidedByCustomer ? 'customer' : 'user'}
        />
        {config.imageProvidedByCustomer && (
          <div className="space-y-2">
            <SelectMenu
              label={'Task Template Variable for Image URL'}
              options={templateVariablesOptions}
              selectedOptionId={config.imageUrlTemplateVariable ?? ''}
              setSelected={(variable: string) =>
                updateProperty({ ...config, imageUrlTemplateVariable: variable })
              }
            />
            <Switch
              altDescription="Disable Image Editing"
              label="Disable Image Editing"
              checked={config.disableImageEditing}
              onCheckedChange={checked =>
                updateProperty({
                  ...config,
                  disableImageEditing: checked,
                })
              }
              helperText="Prevent taskers from editing the image while editing the prompt."
            />
          </div>
        )}
        {!config.imageProvidedByCustomer && (
          <div className="rounded-lg shadow bg-neutral-1 h-fit max-w-full my-2 p-4">
            <div className="space-y-2">
              <Typography.Subtitle2>Image Attachment Sources</Typography.Subtitle2>
              <Switch
                altDescription="Enable Image Service"
                label="Enable Image Service"
                checked={config.enableMmImageService}
                onCheckedChange={checked =>
                  updateProperty({ ...config, enableMmImageService: checked })
                }
              />
              <Typography.Paragraph2 color="text-neutral-500">
                Allow taskers to submit images from the Scale image database or validated internet
                search during the chat session.
              </Typography.Paragraph2>
              <Switch
                altDescription="Enable Image URLs"
                label="Enable Image URLs"
                checked={config.useImageUrls}
                onCheckedChange={checked =>
                  updateProperty({
                    ...config,
                    useImageUrls: checked,
                  })
                }
              />
              <Typography.Paragraph2 color="text-neutral-500">
                Allow taskers to submit external image URLs during the chat session.
              </Typography.Paragraph2>
              {(config.useImageUrls || config.enableMmImageService) && (
                <div className="space-y-2">
                  <Typography.Subtitle2>Image Attachment Enforcement</Typography.Subtitle2>
                  <SelectMenu
                    options={[
                      {
                        id: 'promptOnly',
                        name: 'Enable image attachments in the prompt for the first turn only',
                      },
                      {
                        id: 'promptAndMultiTurn',
                        name: 'Enable image attachments in the prompt for all turns',
                      },
                    ]}
                    selectedOptionId={
                      config.useImageUrlsOnMultiTurn ? 'promptAndMultiTurn' : 'promptOnly'
                    }
                    setSelected={val =>
                      updateProperty({
                        ...config,
                        useImageUrlsOnMultiTurn: val === 'promptAndMultiTurn',
                        // if images are only enabled on the first turn, also update requireImageUrlsOnlySingleTurn if it's false
                        requireImageUrlsOnlySingleTurn:
                          val === 'promptOnly' &&
                          config.isImageUrlRequired &&
                          config.requireImageUrlsOnlySingleTurn === false
                            ? true
                            : config.requireImageUrlsOnlySingleTurn,
                      })
                    }
                  />
                  <Switch
                    altDescription="Make Image Service/Url Attachments Required"
                    label="Make Image Service/Url Attachments Required"
                    checked={config.isImageUrlRequired}
                    onCheckedChange={checked =>
                      updateProperty({
                        ...config,
                        isImageUrlRequired: checked,
                      })
                    }
                  />
                  {config.isImageUrlRequired && (
                    <SelectMenu
                      options={[
                        ...(config.useImageUrlsOnMultiTurn
                          ? [
                              {
                                id: 'promptAndMultiTurn',
                                name: 'Require image service/url attachments in the prompt for all turns',
                              },
                            ]
                          : []),
                        {
                          id: 'promptOnly',
                          name: 'Require image service/url attachments in the prompt for the first turn only',
                        },
                      ]}
                      selectedOptionId={
                        config.requireImageUrlsOnlySingleTurn ? 'promptOnly' : 'promptAndMultiTurn'
                      }
                      setSelected={val =>
                        updateProperty({
                          ...config,
                          requireImageUrlsOnlySingleTurn: val === 'promptOnly',
                        })
                      }
                    />
                  )}
                  <Switch
                    altDescription="Limit to 1 Image"
                    label="Limit to 1 Image"
                    checked={config.maxAttachments === 1}
                    onCheckedChange={checked =>
                      updateProperty({ ...config, maxAttachments: checked ? 1 : undefined })
                    }
                  />
                  {config.enableMmImageService && false && (
                    // TODO: show this when the feature is ready
                    <Switch
                      altDescription="Allow NSFW Images from Image Service"
                      label="Allow NSFW Images from Image Service"
                      checked={config.allowNSFWImagesFromDB}
                      onCheckedChange={checked =>
                        updateProperty({ ...config, allowNSFWImagesFromDB: checked })
                      }
                    />
                  )}
                  {false && (
                    // TODO: show this when the feature is ready
                    <Switch
                      altDescription="Drag and drop disabled"
                      label="Drag and drop disabled"
                      checked={config.isDragAndDropDisabled}
                      onCheckedChange={checked =>
                        updateProperty({ ...config, isDragAndDropDisabled: checked })
                      }
                    />
                  )}
                </div>
              )}
              <div className="rounded-lg shadow bg-neutral-1 h-fit max-w-full my-2 p-4 space-y-2">
                <Typography.Subtitle2>Validation</Typography.Subtitle2>
                <SelectMenu
                  options={Object.entries(ImageExtension).map(([key, value]) => ({
                    id: value,
                    name: key,
                  }))}
                  selectedOptionId={allowedExtensions}
                  setSelected={selected => {
                    setAllowedExtensions(selected as ImageExtension[]);
                    updateProperty(
                      produce(config, (draft: MultiTurnInteractionConfiguration) => {
                        _.set(draft, 'imageValidatorSettings.allowedExtensions', selected);
                      }),
                    );
                  }}
                  label="Allowed Image Extensions"
                  multiple
                />
                {config.enableMmImageService && (
                  // image category filtering only works with image service
                  <SelectMenu
                    options={Object.keys(ImageCategory)
                      .map(category => ({
                        id: ImageCategory[category as keyof typeof ImageCategory],
                        name: category,
                      }))
                      .filter(
                        option => !DEFAULT_CATEGORY_BLOCKLIST.includes(option.id as ImageCategory),
                      )}
                    selectedOptionId={allowedCategories}
                    setSelected={selected => {
                      setAllowedCategories(selected);
                      updateProperty(
                        produce(config, (draft: MultiTurnInteractionConfiguration) => {
                          _.set(
                            draft,
                            'imageValidatorSettings.allowedCategories',
                            selected as ImageCategory[],
                          );
                          return draft;
                        }),
                      );
                    }}
                    label="Allowed Image Categories"
                    multiple
                  />
                )}
                <div className="flex flex-col gap-2">
                  <TextField
                    id="minDim"
                    label="Minimum Dimensions of Image, In Pixels"
                    type="number"
                    value={config.imageValidatorSettings?.minDimensionsPx ?? ''}
                    onChange={v =>
                      updateProperty(
                        produce(config, (draft: MultiTurnInteractionConfiguration) => {
                          _.set(
                            draft,
                            'imageValidatorSettings.minDimensionsPx',
                            v.target.value.length
                              ? Number(v.target.value.replace(/\D/g, ''))
                              : undefined,
                          );
                          return draft;
                        }),
                      )
                    }
                    labelDescription="Image must be at least this in either height or width"
                  />
                  <TextField
                    id="maxDim"
                    label="Maximum Dimensions of Image, In Pixels"
                    value={config.imageValidatorSettings?.maxDimensionsPx}
                    type="number"
                    onChange={v =>
                      updateProperty(
                        produce(config, (draft: MultiTurnInteractionConfiguration) => {
                          _.set(
                            draft,
                            'imageValidatorSettings.maxDimensionsPx',
                            v.target.value.length
                              ? Number(v.target.value.replace(/\D/g, ''))
                              : undefined,
                          );
                          return draft;
                        }),
                      )
                    }
                    labelDescription="Image must be at most this in both height and width"
                  />
                  <TextField
                    id="minSize"
                    label="Minimum Image File Size, In KB"
                    value={config.imageValidatorSettings?.minFileSizeKB}
                    type="number"
                    onChange={v =>
                      updateProperty(
                        produce(config, (draft: MultiTurnInteractionConfiguration) => {
                          _.set(
                            draft,
                            'imageValidatorSettings.minFileSizeKB',
                            v.target.value.length
                              ? Number(v.target.value.replace(/\D/g, ''))
                              : undefined,
                          );
                          return draft;
                        }),
                      )
                    }
                  />
                  <TextField
                    id="maxSize"
                    label="Maximum Image File Size, In KB"
                    value={config.imageValidatorSettings?.maxFileSizeKB}
                    type="number"
                    onChange={v =>
                      updateProperty(
                        produce(config, (draft: MultiTurnInteractionConfiguration) => {
                          _.set(
                            draft,
                            'imageValidatorSettings.maxFileSizeKB',
                            v.target.value.length
                              ? Number(v.target.value.replace(/\D/g, ''))
                              : undefined,
                          );
                          return draft;
                        }),
                      )
                    }
                  />
                </div>
                <SelectMenu
                  label={'Domain Restrictions'}
                  options={Object.values(DomainRestriction).map(domain => ({
                    id: domain,
                    name: domain,
                  }))}
                  selectedOptionId={domainRestriction}
                  setSelected={(variable: string) => {
                    setDomainRestriction(variable as DomainRestriction);
                    updateProperty(
                      produce(config, (draft: MultiTurnInteractionConfiguration) => {
                        _.set(draft, 'imageValidatorSettings.blockedDomainsList', undefined);
                        _.set(draft, 'imageValidatorSettings.allowedDomainsList', undefined);
                      }),
                    );
                  }}
                />
                {domainRestriction === DomainRestriction.BlockedDomains && (
                  <TextField
                    id="BlockedDomains"
                    label="Blocked Domains, separated by comma"
                    value={config.imageValidatorSettings?.blockedDomainsList?.join(', ')}
                    onChange={e =>
                      updateProperty(
                        produce(config, (draft: MultiTurnInteractionConfiguration) => {
                          const blockedDomains = e.target.value.split(/[\n, ]+/);
                          const cleanedBlockedDomains =
                            blockedDomains.length === 1 && blockedDomains[0] === ''
                              ? []
                              : blockedDomains;
                          _.set(
                            draft,
                            'imageValidatorSettings.blockedDomainsList',
                            cleanedBlockedDomains,
                          );
                          _.set(draft, 'imageValidatorSettings.allowedDomainsList', undefined);
                          return draft;
                        }),
                      )
                    }
                  />
                )}
                {domainRestriction === DomainRestriction.AllowedDomains && (
                  <TextField
                    id="AllowedDomains"
                    label="Allowed Domains, separated by comma"
                    value={config.imageValidatorSettings?.allowedDomainsList?.join(', ')}
                    onChange={e =>
                      updateProperty(
                        produce(config, (draft: MultiTurnInteractionConfiguration) => {
                          const allowedDomains = e.target.value.split(/[\n, ]+/);
                          const cleanedAllowedDomains =
                            allowedDomains.length === 1 && allowedDomains[0] === ''
                              ? []
                              : allowedDomains;
                          _.set(
                            draft,
                            'imageValidatorSettings.allowedDomainsList',
                            cleanedAllowedDomains,
                          );
                          _.set(draft, 'imageValidatorSettings.blockedDomainsList', undefined);
                          return draft;
                        }),
                      )
                    }
                  />
                )}
                <TextField
                  id="BlockedSearchKeywords"
                  label="Blocked Search Keywords, separated by comma"
                  value={config.imageValidatorSettings?.blockedKeywordList?.join(', ')}
                  onChange={e =>
                    updateProperty(
                      produce(config, (draft: MultiTurnInteractionConfiguration) => {
                        const blockedKeywords = e.target.value.split(/[\n, ]+/);
                        const cleanedBlockedKeywords =
                          blockedKeywords.length === 1 && blockedKeywords[0] === ''
                            ? []
                            : blockedKeywords;
                        _.set(
                          draft,
                          'imageValidatorSettings.blockedKeywordList',
                          cleanedBlockedKeywords,
                        );
                        return draft;
                      }),
                    )
                  }
                />
                {config.enableMmImageService && (
                  // image license filtering only works with image service
                  <ImageServiceConfigurator
                    existingValue={config}
                    updateProperty={updateProperty}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </WrappingComponent>
  );
};

export default ImageReferenceBuilder;
